import React, { useRef } from "react";
import clsx from "clsx";

import useOutsideClick from "@app/util/outside-click";
import ChevronDown from "@assets/icons/chevron-down-sharp.svg";

import { ClickableComponent } from "../clickable";
import styles from "./dropdown-component.module.scss";

export interface IDropdownComponentProps {
  animated?: boolean;
  children: React.ReactNode;
  hasSelectedItems?: boolean;
  iconLeft?: string;
  iconRight?: string;
  isOpen?: boolean;
  isShareDropDown?: boolean;
  isFilterDropDown?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: string;
  isRounded?: boolean;
}

const DropdownComponent = (props: IDropdownComponentProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const zIndexWhenOpen = 1021;

  useOutsideClick(wrapperRef, () => {
    if (props.isOpen) {
      props.setIsOpen(false);
    }
  });

  const getVariant = () => {
    if (props.isFilterDropDown) {
      return "white";
    }
    if (props.isOpen) {
      if (props.isShareDropDown) {
        return "secondary";
      }
      return "primary-inverted";
    }
    if (props.hasSelectedItems) {
      return "primary-brand";
    }

    return "secondary";
  };

  const onButtonClick = () => {
    if (props.isOpen) {
      props.setIsOpen(false);
    } else {
      props.setIsOpen(true);
    }
  };

  return (
    <div
      className={clsx(
        styles.dropdown,
        props.isOpen && styles.open,
        props.isShareDropDown && styles.share,
        props.isRounded && styles.isRounded
      )}
      ref={wrapperRef}
    >
      <div
        className={clsx(
          styles.dropdownChild,
          props.hasSelectedItems && !props.isOpen && styles["dropdownChild--has-selected"],
          props.isOpen && styles["dropdownChild--open"],
          props.isShareDropDown && styles["dropdownChild--share"],
          styles[`dropdownChild--${getVariant()}`]
        )}
      >
        <ClickableComponent
          onClick={onButtonClick}
          title={props.title}
          variant={getVariant()}
          zIndex={props.isOpen ? zIndexWhenOpen : undefined}
          iconRight={props.iconRight ?? ChevronDown}
          iconLeft={props.iconLeft}
          isRounded={props.isRounded}
          isFilterDropDown={props.isFilterDropDown}
        />
      </div>
      <div
        data-cy="dropdown-content"
        className={clsx(styles.content, props.isOpen && styles.open, props.isShareDropDown && styles.share)}
      >
        {props.children}
      </div>
    </div>
  );
};

export { DropdownComponent };
